import axios from "axios";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import { saveFlightData, saveTicketSelectedFilters } from "../store/actions";
import BASE_URL from "../constants/baseUrls";
import { useEffect, useRef } from "react";

const useFlightList = (domesticFlightInformation, uid, ticketSort, ticketSelectedFilters, setRequestFlag, setLoading, setIsError) => {
    //general variables
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useTranslation();
    const accessToken = localStorage.getItem("accessToken");
    const ticketType = domesticFlightInformation?.returnDate ? "return" : "onWay";
    const abortControllerRef = useRef(null);
    const isMountedRef = useRef(true);

    const getFlightList = async () => {
        setLoading(true);
        try {
            setIsError(false);
            const params = createFlightListParams();
            const response = await axios.get(`${BASE_URL}/api/v2/reserve/flight/list/`, {
                params,
                headers: {
                    Authorization: accessToken ? `Bearer ${accessToken}` : "",
                },
                // signal,
            });
            if (isMountedRef.current) {
                // Process the response and update state
                dispatch(saveFlightData(response.data || null));
            }
        } catch (error) {
            if (isMountedRef.current) {
                // Only handle errors if the component is still mounted
                dispatch(saveFlightData(null));
                dispatch(saveTicketSelectedFilters(null));
                toast.error(t("errorMessage"), { toastId: "custom-id-yes" });
                setIsError(true);
            }
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
                setRequestFlag(false);
                searchParams.delete("action");
                setSearchParams(searchParams);
                abortControllerRef.current = null;
            }
        }
    };

    // Clean up on unmount
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const createFlightListParams = () => {
        const baseParams = buildBaseParams();
        const filterParams = buildFilterParams();

        return { ...baseParams, ...filterParams };
    };

    const buildBaseParams = () => ({
        departure: domesticFlightInformation?.departure.id,
        destination: domesticFlightInformation?.destination.id,
        round_trip: ticketType === "return",
        date: domesticFlightInformation?.departureDate,
        return_date: domesticFlightInformation?.returnDate || null,
        adult: domesticFlightInformation?.personCounter?.adult,
        child: domesticFlightInformation?.personCounter?.child,
        infant: domesticFlightInformation?.personCounter?.infant,
        sort: ticketSort,
        uid,
    });

    const buildFilterParams = () => {
        const isRoundTrip = domesticFlightInformation?.hasSecondTicket;

        return isRoundTrip
            ? {
                  return_price_from: ticketSelectedFilters?.prices?.[0],
                  return_price_to: ticketSelectedFilters?.prices?.[1],
                  return_depart_time_from: ticketSelectedFilters?.departure_times?.[0],
                  return_depart_time_to: ticketSelectedFilters?.departure_times?.[1],
                  return_airlines: ticketSelectedFilters?.airlines,
              }
            : {
                  price_from: ticketSelectedFilters?.prices?.[0],
                  price_to: ticketSelectedFilters?.prices?.[1],
                  depart_time_from: ticketSelectedFilters?.departure_times?.[0],
                  depart_time_to: ticketSelectedFilters?.departure_times?.[1],
                  airlines: ticketSelectedFilters?.airlines,
              };
    };
    return { getFlightList };
};

export default useFlightList;
